import { supabase } from "@/integrations/supabase/client";

type AuthLikeError = {
  message?: string | null;
  status?: number | string | null;
  code?: string | null;
  name?: string | null;
};

const RETRIABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const RETRIABLE_MESSAGE_PATTERNS = [
  /request timed out/i,
  /timeout/i,
  /context deadline exceeded/i,
  /failed to fetch/i,
  /fetch failed/i,
  /network request failed/i,
  /gateway timeout/i,
  /temporarily unavailable/i,
];

const INVALID_CREDENTIAL_PATTERNS = [
  /invalid login credentials/i,
  /email not confirmed/i,
  /invalid credentials/i,
];

const INVALID_REFRESH_TOKEN_PATTERNS = [
  /invalid refresh token/i,
  /refresh token not found/i,
  /refresh_token_not_found/i,
];

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  const maybeError = error as AuthLikeError | null;
  return maybeError?.message ?? "";
}

function getErrorStatus(error: unknown) {
  const maybeError = error as AuthLikeError | null;
  const status = maybeError?.status;

  if (typeof status === "number") return status;
  if (typeof status === "string") {
    const parsed = Number(status);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function isRetriableAuthError(error: unknown) {
  const message = getErrorMessage(error);
  const status = getErrorStatus(error);

  return (
    (status !== null && RETRIABLE_STATUS_CODES.has(status)) ||
    RETRIABLE_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
}

export function isInvalidRefreshTokenError(error: unknown) {
  const message = getErrorMessage(error);
  return INVALID_REFRESH_TOKEN_PATTERNS.some((pattern) => pattern.test(message));
}

export function getFriendlyAuthErrorMessage(error: unknown, fallback = "Noe gikk galt. Prøv igjen.") {
  const message = getErrorMessage(error);

   if (isInvalidRefreshTokenError(error)) {
    return "Økten din var ugyldig og ble nullstilt. Prøv å logge inn på nytt.";
  }

  if (isRetriableAuthError(error)) {
    return "Innloggingen brukte for lang tid. Prøv igjen om noen sekunder.";
  }

  if (INVALID_CREDENTIAL_PATTERNS.some((pattern) => pattern.test(message))) {
    return "Feil e-post eller passord.";
  }

  return message || fallback;
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("request timed out")), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export async function getSessionWithTimeout(ms = 5000) {
  return withTimeout(supabase.auth.getSession(), ms);
}

export async function clearLocalAuthSession() {
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // ignore cleanup failures
  }
}

export async function signInWithRetry(email: string, password: string, maxAttempts = 3) {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const { error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        12000,
      );

      if (!error) {
        return { error: null };
      }

      lastError = error;

      if (isInvalidRefreshTokenError(error)) {
        await clearLocalAuthSession();
      }

      if (!isRetriableAuthError(error) || attempt === maxAttempts) {
        return { error };
      }
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e));

      if (isInvalidRefreshTokenError(lastError)) {
        await clearLocalAuthSession();
      }

      if (!isRetriableAuthError(lastError) || attempt === maxAttempts) {
        return { error: lastError };
      }
    }

    await sleep(500 * attempt);
  }

  return { error: lastError };
}